import { createSlice } from "@reduxjs/toolkit";
import SimpleReactValidator from "simple-react-validator";
import {
  EnumAttributeType,
  EnumPageMode,
  EnumQuestionTypesType,
  EnumStatus,
} from "../../../common/enums/common.enums";
import {
  DefaultTextDTO,
  DropDownDTO,
  LanguageDTO,
  QuestionChoiceDTO,
  QuestionDTO,
  QuestionParameterDTO,
  QuestionTypeDTO,
  SourcesDTO,
  StandardDTO,
  StatusDTO,
  TranslatedTextDTO,
} from "../../../common/types/common.dto.types";
import { PageModeType } from "../../../common/types/common.page.type";
import { QuestionPageType } from "./QuestionsPage.types";

// ==============================|| VIEWS - PAGES - QUESTION - REDUCER ||============================== //

// initial state
const initialState = {
  IsInitialDataLoaded: false,
  EffectivePeriodFrom: false,
  EffectivePeriodTo: false,
  PageMode: {
    PageMode: EnumPageMode.Default,
    IsShowSaveBtn: false,
    IsDisable: true,
    IsShowCloseBtn: false,
    IsShowUpdateBtn: false,
    IsShowCancelBtn: false,
    IsShowDraftBtn: false,
    IsShowEditBtn: false,
    IsShowPublishedBtn: false,
  } as PageModeType,

  LanguageId: 1,
  TranslatedText: "",
  RequirementText: "",
  Question: {
    Id: 0,
    AdditionalComments: true,
    NotApplicable: true,
    ProductImpact: false,
    QuestionTypeId: 0,
    ValidFrom: "",
    ValidTo: "",
    QuestionChoices: [] as Array<QuestionChoiceDTO>,
    Version: 0.1,
    Status: EnumStatus.Draft,
    StatusNavigation: { Status1: "Draft" } as StatusDTO,
    ImageUpload: EnumAttributeType.Allow,
    DocumentUpload: EnumAttributeType.Allow,
    RequirementText: {
      DefaultText1: "",
      TranslatedTexts: [] as Array<TranslatedTextDTO>,
    } as DefaultTextDTO,
    QuestionType: {
      Code: "",
      Id: 0,
      ModifiedBy: "",
      Name: "",
      Status: EnumStatus.Published,
      Version: 0,
    } as QuestionTypeDTO,
    StandardId: 0,
    Standard: { Name: "", Id: 0 } as StandardDTO,
    QuestionParameters: [] as Array<QuestionParameterDTO>,
    ReferanceList: [] as Array<DropDownDTO>,
    SystemList: [] as Array<DropDownDTO>,
    SubSystemList: [] as Array<DropDownDTO>,
    SubjectList: [] as Array<DropDownDTO>,
    SectionList: [] as Array<DropDownDTO>,
    QuestionTypeList: [] as Array<DropDownDTO>,
    LanguageList: [] as Array<LanguageDTO>,
    SequenceNumber: 0,
    QuestionCategoryId: null,
    QuestionCategoryDropdownList: [] as Array<DropDownDTO>,
    IsFirstQuestion: false,
    IsLastQuestion: false,
    IsChoiceDeactivated: false,
  } as QuestionDTO,
  HeaderText: "",
  Validator: new SimpleReactValidator(),
  ShowErrorMsg: false,
  ReferenceId: 0,
  SystemId: 0,
  SubSystemId: 0,
  SubjectId: 0,
  SectionId: 0,
  ReferanceList: [] as Array<DropDownDTO>,
  SystemList: [] as Array<DropDownDTO>,
  SubSystemList: [] as Array<DropDownDTO>,
  SubjectList: [] as Array<DropDownDTO>,
  SectionList: [] as Array<DropDownDTO>,
  ViewNavigationArrows: false,
  AllQuestionCount: 0,
  QuestionCategoryDropdownList: [] as Array<DropDownDTO>,
  TempQuestionCategoryDropdownList: [] as Array<DropDownDTO>,
  QuestionCategoryChoiceList: [] as Array<QuestionChoiceDTO>,
  IsDropdownCategoryDataUpdate: false,
  IsCategoryChoiceDataUpdate: false,
} as QuestionPageType;

export const questionSlice = createSlice({
  name: "question",
  initialState: initialState,
  reducers: {
    resetQuestion: (state: QuestionPageType) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        Question: initialState.Question,
      };
    },
    setStandardId: (
      state: QuestionPageType,
      action: { payload: number; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        Question: { ...state.Question, StandardId: action.payload },
      };
    },
    setStandard: (
      state: QuestionPageType,
      action: { payload: StandardDTO; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        StandardName: action.payload.Name,
      };
    },
    setLanguageId: (
      state: QuestionPageType,
      action: { payload: number; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        LanguageId: action.payload,
      };
    },
    setLanguageList: (
      state: QuestionPageType,
      action: { payload: Array<LanguageDTO>; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        LanguageList: action.payload,
      };
    },
    setQuestion: (
      state: QuestionPageType,
      action: { payload: QuestionDTO; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
        Question: action.payload,
        QuestionCategoryDropdownList:
          action.payload.QuestionCategoryDropdownList,
        TempQuestionCategoryDropdownList:
          action.payload.QuestionCategoryDropdownList,
      };
    },
    changePageMode: (
      state: QuestionPageType,
      action: { payload: EnumPageMode; type: string }
    ) => {
      if (EnumPageMode.ViewMode === action.payload) {
        return {
          ...state,
          PageMode: {
            ...state.PageMode,
            PageMode: EnumPageMode.ViewMode,
            IsDisable: true,
            IsShowCloseBtn: true,
            IsShowEditBtn: true,
            IsShowDeactivateBtn: true,
            IsShowCancelBtn: false,
            IsShowDraftBtn: false,
            IsShowPublishedBtn: false,
            IsShowUpdateBtn: false,
            IsShowSaveBtn: false,
          },
          IsDropdownCategoryDataUpdate: false,
          IsCategoryChoiceDataUpdate: false,
        };
      } else if (EnumPageMode.EditMode === action.payload) {
        return {
          ...state,
          PageMode: {
            ...state.PageMode,
            PageMode: EnumPageMode.EditMode,
            IsDisable: false,
            IsShowCancelBtn: true,
            IsShowDeactivateBtn: true,
            IsShowDraftBtn: true,
            IsShowPublishedBtn: true,
            IsShowCloseBtn: false,
            IsShowEditBtn: false,
            IsShowSaveBtn: false,
            IsShowUpdateBtn: false,
          },
          IsDropdownCategoryDataUpdate: false,
          IsCategoryChoiceDataUpdate: false,
        };
      } else if (EnumPageMode.AddMode === action.payload) {
        return {
          ...state,
          PageMode: {
            ...state.PageMode,
            PageMode: EnumPageMode.AddMode,
            IsDisable: false,
            IsShowCancelBtn: true,
            IsShowDraftBtn: true,
            IsShowPublishedBtn: true,
            IsShowSaveBtn: false,
            IsShowUpdateBtn: false,
            IsShowCloseBtn: false,
            IsShowEditBtn: false,
            IsShowDeactivateBtn: false,
          },
          IsDropdownCategoryDataUpdate: false,
          IsCategoryChoiceDataUpdate: false,
          // Standard: initialState.Standard,
        };
      }
    },
    setSources: (
      state: QuestionPageType,
      action: { payload: SourcesDTO; type: string }
    ) => {
      return {
        ...state,
        Question: {
          ...state.Question,
        },
        ReferenceId: 0,
        SystemId: 0,
        SubSystemId: 0,
        SubjectId: 0,
        SectionId: 0,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },
    setQuestionPageInitialData: (
      state: QuestionPageType,
      action: { payload: QuestionPageType; type: string }
    ) => {
      return {
        ...state,
        IsInitialDataLoaded: action.payload.IsInitialDataLoaded,
        ReferanceList: action.payload.ReferanceList,
        SystemList: action.payload.SystemList,
        SubSystemList: action.payload.SubSystemList,
        SubjectList: action.payload.SubjectList,
        SectionList: action.payload.SectionList,
        QuestionTypeList: action.payload.QuestionTypeList,
        LanguageList: action.payload.LanguageList,
        // QuestionCategoryDropdownList:
        //   action.payload.QuestionCategoryDropdownList,
        Question: {
          ...state.Question,
          Id: action.payload.Question.Id,
          StandardId: action.payload.Question.StandardId,
          Standard: action.payload.Question.Standard,
        },
        Standard: action.payload.Question.Standard,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },
    setQuestionStatus: (
      state: QuestionPageType,
      action: { payload: QuestionDTO; type: string }
    ) => {
      return {
        ...state,
        Question: {
          ...state.Question,
          Status: action.payload.Status,
          StatusNavigation: action.payload.StatusNavigation,
        },
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },
    setInitialDataLoad: (
      state: QuestionPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        IsInitialDataLoaded: action.payload,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },
    setViewNavigationArrows: (
      state: QuestionPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        ViewNavigationArrows: action.payload,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },
    setAllQuestionCount: (
      state: QuestionPageType,
      action: { payload: number; type: string }
    ) => {
      return {
        ...state,
        AllQuestionCount: action.payload,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: false,
      };
    },

    setQuestionDropdownCategories: (
      state: QuestionPageType,
      action: { payload: Array<DropDownDTO>; type: string }
    ) => {
      return {
        ...state,
        TempQuestionCategoryDropdownList: action.payload,
        IsDropdownCategoryDataUpdate: true,
        IsCategoryChoiceDataUpdate: false,
      };
    },

    setQuestionCategoryChoices: (
      state: QuestionPageType,
      action: { payload: Array<QuestionChoiceDTO>; type: string }
    ) => {
      return {
        ...state,
        IsDropdownCategoryDataUpdate: false,
        IsCategoryChoiceDataUpdate: true,
        QuestionCategoryChoiceList: action.payload,
      };
    },

    resetQuestionCategories: (
      state: QuestionPageType,
      action: { payload: Array<DropDownDTO>; type: string }
    ) => {
      return {
        ...state,
        TempQuestionCategoryDropdownList: action.payload,
        IsDropdownCategoryDataUpdate: true,
        IsCategoryChoiceDataUpdate: false,
      };
    },
  },
});

export const {
  setLanguageId,
  setLanguageList,
  setQuestion,
  changePageMode,
  resetQuestion,
  setStandardId,
  setSources,
  setQuestionPageInitialData,
  setQuestionStatus,
  setInitialDataLoad,
  setViewNavigationArrows,
  setAllQuestionCount,
  setQuestionDropdownCategories,
  setQuestionCategoryChoices,
  resetQuestionCategories,
} = questionSlice.actions;
export const questionReducer = questionSlice.reducer;
export default false;
